/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React from "react";
import RedoIcon from "dc-icons/consumption/s_redo_18.svg";
import UndoIcon from "dc-icons/consumption/s_undo_18.svg";
import commandManager from "undoRedo/CommandManager";
import UndoRedoCss from "./undoRedo.css";
import { KEYS_TO_DISPLAY_LABELS } from "../../../shortcutManager/constants";
import { isWindows } from "dc-preview-utils/src/Utils/DeviceUtils";

const UndoRedoIconWrapper = ({ Icon, disabled }) => (
  <div className={disabled ? UndoRedoCss.iconDisabled : UndoRedoCss.iconEnabled}>
    <Icon />
  </div>
);

export function getUndoRedoActions({ intl, isPDF, shouldUnblockUIFeatures }) {
  const undoLabel = intl.formatMessage({ id: "label.undo" });
  const undoShortcut = `${isWindows() ? KEYS_TO_DISPLAY_LABELS.ctrl : KEYS_TO_DISPLAY_LABELS.meta} ${
    KEYS_TO_DISPLAY_LABELS.z
  }`;
  const undoTooltipWithShortcut = `${undoLabel} (${undoShortcut})`;

  const redoLabel = intl.formatMessage({ id: "label.redo" });
  const redoShortcut = `${isWindows() ? KEYS_TO_DISPLAY_LABELS.ctrl : KEYS_TO_DISPLAY_LABELS.meta} ${
    KEYS_TO_DISPLAY_LABELS.shiftMac
  } ${KEYS_TO_DISPLAY_LABELS.z}`;
  const redoTooltipWithShortcut = `${redoLabel} (${redoShortcut})`;

  const undoRedoActions = [];
  if (isPDF && shouldUnblockUIFeatures) {
    const undoDisabled = !commandManager.hasUndoCommand();
    const redoDisabled = !commandManager.hasRedoCommand();
    undoRedoActions.push(
      {
        id: "undo",
        label: undoTooltipWithShortcut,
        ariaLabel: undoLabel,
        Icon: <UndoRedoIconWrapper Icon={UndoIcon} disabled={undoDisabled} />,
        onClick: () => commandManager.undo(),
        disabled: undoDisabled
      },
      {
        id: "redo",
        label: redoTooltipWithShortcut,
        ariaLabel: redoLabel,
        Icon: <UndoRedoIconWrapper Icon={RedoIcon} disabled={redoDisabled} />,
        onClick: () => commandManager.redo(),
        disabled: redoDisabled
      }
    );
  }

  return undoRedoActions;
}
